<template>
  <wizard-shell
    :value="dialog"
    icon="mdi-api"
    :title="isEditMode ? 'Edit API Data Source' : 'API Data Source'"
    :subtitle="register ? register.name : ''"
    :steps="steps"
    :current-step="step"
    :max-width="shellMaxWidth"
    :height="shellHeight"
    :progress="progress"
    :close-disabled="saving"
    close-disabled-title="Saving the source…"
    close-label="Close API data source wizard"
    flush
    clickable-steps
    dialog-class="riw ras-wizard"
    step-variant="pill"
    @input="onShellInput"
    @close="confirmClose"
    @step-click="goToStep">
    <template #sidebar-extras>
      <import-rail-summary :stats="railStats" :info-rows="railInfoRows" />
    </template>

    <!-- Re-keyed per opening so every step starts fresh. Steps 1–3 stay mounted (v-show) so going
         Back keeps the test result, the preview and the mapping editor's state. -->
    <div :key="sessionKey" class="ras-steps">
      <!-- 1. Configure API -->
      <div v-show="step === API_STEP.CONFIGURE" class="riw-panel-body">
        <shared-api-config-step v-model="config" />
      </div>

      <!-- 2. Test & Preview -->
      <div v-show="step === API_STEP.TEST" class="riw-panel-body">
        <shared-api-test-preview-step
          ref="testPreviewStep"
          :config="config"
          :api-service="apiService"
          @test-success="onTestSuccess"
          @preview-loaded="onPreviewLoaded"
          @update:responseDataPath="onResponseDataPathUpdate" />
      </div>

      <!-- 3. Map Fields -->
      <field-mapping
        v-if="previewData"
        v-show="step === API_STEP.MAP"
        :key="previewGeneration"
        ref="fieldMapping"
        :excel-columns="previewData.headers || []"
        :excel-data="previewData.data || []"
        :register="register"
        :initial-mapping="mapping"
        :register-row-count="registerRowCount"
        :schedule-frequency="schedule.frequency"
        :identity-required="identityRequired"
        source-label="API"
        hide-actions
        @status="mappingStatus = $event"
        @mapping-complete="onMappingComplete"
        @switch-to-manual="switchToManual" />
      <div v-if="step === API_STEP.MAP && !previewData" class="riw-panel-body">
        <div class="ras-empty">
          <v-icon size="44">mdi-table-off</v-icon>
          <div>No preview loaded, so there are no API columns to map yet.</div>
          <div v-if="fieldCount">The saved mapping ({{ fieldCount }} {{ fieldCount === 1 ? 'field' : 'fields' }}) is kept as it is.</div>
          <v-btn small outlined color="primary" @click="goToStep(API_STEP.TEST)">
            <v-icon left small>mdi-arrow-left</v-icon>Back to Test &amp; Preview
          </v-btn>
        </div>
      </div>

      <!-- 4. Schedule -->
      <div v-if="step === API_STEP.SCHEDULE" class="riw-panel-body">
        <api-schedule-step
          v-model="schedule"
          :blockers="scheduleBlockerList"
          :register-unset="registerUnset"
          :identity-label="identityName"
          :identity-column="identityColumn"
          @go-step="goToStep" />
      </div>

      <!-- 5. Review -->
      <div v-if="step === API_STEP.REVIEW" class="riw-panel-body">
        <api-review-step
          :cards="reviewCardList"
          :blockers="saveBlockerList"
          :save-error="saveError"
          :register-unset="registerUnset"
          :register-name="register ? register.name : ''"
          :schedule="schedule"
          :has-identity="hasIdentity"
          :identity-label="identityName"
          @go-step="goToStep" />
      </div>
    </div>

    <template #footer>
      <v-btn text :disabled="saving" @click="confirmClose">Cancel</v-btn>
      <span class="riw-foot-spacer"></span>
      <span v-if="footHint" class="riw-foot-hint" :class="`riw-foot-hint--${footHint.kind}`">
        <v-icon>{{ footHint.icon }}</v-icon>{{ footHint.text }}
      </span>
      <v-btn v-if="step > API_STEP.CONFIGURE" text :disabled="saving" @click="back">
        <v-icon left>mdi-arrow-left</v-icon>Back
      </v-btn>
      <v-btn
        v-if="step < API_STEP.REVIEW"
        color="primary"
        depressed
        :disabled="!canContinue"
        @click="next">
        {{ primaryText }}<v-icon right>mdi-arrow-right</v-icon>
      </v-btn>
      <v-btn
        v-else
        color="primary"
        depressed
        :loading="saving"
        :disabled="!canContinue"
        @click="save">
        <v-icon left>mdi-content-save</v-icon>{{ primaryText }}
      </v-btn>
    </template>
  </wizard-shell>
</template>

<script>
  import WizardShell from '@/features/Share/components/wizard/WizardShell.vue';
  import { SharedApiConfigStep, SharedApiTestPreviewStep } from '@/features/Share/components/apiDataSource';
  import { log, warn, error } from '@/utils/debugLogger';
  import apiDataSourceService from '../../services/apiDataSourceService';
  import registerService from '../../services/registerService';
  import FieldMapping from './FieldMapping.vue';
  import ImportRailSummary from './importWizard/ImportRailSummary.vue';
  import { footerHint, isBlocking } from './importWizard/fieldMappingPresentation';
  import './importWizard/styles';
  import ApiScheduleStep from './apiSource/ApiScheduleStep.vue';
  import ApiReviewStep from './apiSource/ApiReviewStep.vue';
  import './apiSource/styles';
  import {
    apiFooterHint,
    apiRailInfoRows,
    apiRailStats,
    apiWizardProgress,
    apiWizardSteps,
    primaryLabel,
    reviewCards,
  } from './apiSource/apiSourcePresentation';
  import {
    buildSavePayload,
    createDefaultConfig,
    createDefaultSchedule,
    hasProgress,
    stateFromDataSource,
  } from './apiSource/apiSourceWizardState';
  import {
    API_STEP,
    hasIdentityColumn,
    identityLabel,
    isScheduled,
    mappedFieldCount,
    saveBlockers,
    saveErrorPresentation,
    scheduleBlockers,
  } from '../../modules/apiSourceRules';
  import { REGISTER_KIND, resolveRegisterKind } from '../../modules/registerImportWizardModel';
  import { loadUnsetRegisterRowCount, needsRowCount } from './apiSource/registerRowCount';

  /** Shell sizes (as ExcelUpload): wide for Test & Preview and Map Fields, compact otherwise. */
  const WIDTH_WIDE = '1350px';
  const WIDTH_DEFAULT = '1100px';
  const HEIGHT_WIDE = 'min(860px, 90vh)';
  const HEIGHT_DEFAULT = 'min(760px, calc(100vh - 140px))';

  /**
   * Register API data source wizard on the shared WizardShell (AVA-602 showcase D, plan N29):
   * Configure API → Test & Preview → Map Fields → Schedule → Review. The schedule / identity /
   * register-mode rules (apiSourceRules) are surfaced on the step that fixes them; the server stays
   * the authority and its refusal is shown inline on Review.
   *
   * Masters and Task Import keep SharedApiDataSourceWizard; only its Configure and Test & Preview
   * steps are reused here, unchanged.
   */
  export default {
    name: 'ApiDataSourceWizard',
    components: {
      WizardShell,
      SharedApiConfigStep,
      SharedApiTestPreviewStep,
      FieldMapping,
      ImportRailSummary,
      ApiScheduleStep,
      ApiReviewStep,
    },
    props: {
      value: { type: Boolean, default: false },
      register: { type: Object, default: null },
      editDataSource: { type: Object, default: null },
    },
    data() {
      return {
        API_STEP,
        dialog: false,
        step: API_STEP.CONFIGURE,
        sessionKey: 0,
        saving: false,
        saveError: null,
        testResult: null,
        previewData: null,
        previewGeneration: 0,
        config: createDefaultConfig(),
        mapping: null,
        schedule: createDefaultSchedule(),
        // FieldMapping's `status` (blockers, counts) for the rail and the footer.
        mappingStatus: null,
        // Stored rows of an unset Facility Group register (null = unknown / not needed).
        registerRowCount: null,
        rowCountGeneration: 0,
      };
    },
    computed: {
      isEditMode() {
        return !!this.editDataSource;
      },
      steps() {
        return apiWizardSteps();
      },
      progress() {
        return apiWizardProgress(this.step);
      },
      isWideStep() {
        return this.step === API_STEP.TEST || this.step === API_STEP.MAP;
      },
      shellMaxWidth() {
        return this.isWideStep ? WIDTH_WIDE : WIDTH_DEFAULT;
      },
      shellHeight() {
        return this.isWideStep ? HEIGHT_WIDE : HEIGHT_DEFAULT;
      },
      apiService() {
        return {
          testConnection: (config) => apiDataSourceService.testConnection(config),
          fetchPreview: (config, maxRows, opts) => apiDataSourceService.fetchPreview(config, maxRows, opts),
        };
      },

      registerKind() {
        return resolveRegisterKind(this.register, this.registerRowCount);
      },
      registerUnset() {
        return this.registerKind === REGISTER_KIND.GROUP_UNSET;
      },
      hasIdentity() {
        return hasIdentityColumn(this.mapping);
      },
      identityColumn() {
        return this.hasIdentity ? this.mapping.identityColumn : '';
      },
      identityName() {
        return identityLabel(this.mapping);
      },
      fieldCount() {
        return mappedFieldCount(this.mapping);
      },
      /** "Identity *" on Map Fields while a schedule is chosen (a mode-less register can't have one). */
      identityRequired() {
        return isScheduled(this.schedule.frequency) && !this.registerUnset;
      },
      scheduleBlockerList() {
        return scheduleBlockers({
          hasIdentity: this.hasIdentity,
          registerKind: this.registerKind,
          registerName: this.register ? this.register.name : '',
          registerRowCount: this.registerRowCount,
        });
      },
      scheduleInvalid() {
        return this.scheduleBlockerList.length > 0 && isScheduled(this.schedule.frequency);
      },
      saveBlockerList() {
        return saveBlockers({
          frequency: this.schedule.frequency,
          triggerImmediate: this.schedule.triggerImmediate,
          hasIdentity: this.hasIdentity,
          registerKind: this.registerKind,
          fieldCount: this.fieldCount,
        });
      },
      /** Map Fields may continue: at least one field and no blocking mapping error. */
      mappingReady() {
        if (!this.previewData) return this.fieldCount > 0; // edit without a fresh preview: saved mapping
        const status = this.mappingStatus;
        return !!status && status.configured > 0 && !(status.blockers || []).some(isBlocking);
      },
      canContinue() {
        switch (this.step) {
          case API_STEP.CONFIGURE:
            return !!(this.config.name && String(this.config.name).trim() && this.config.url && String(this.config.url).trim());
          case API_STEP.TEST:
            return !!(this.testResult && this.testResult.success);
          case API_STEP.MAP:
            return this.mappingReady;
          case API_STEP.SCHEDULE:
            return !this.scheduleInvalid;
          default:
            return this.saveBlockerList.length === 0 && !this.saving;
        }
      },
      primaryText() {
        return primaryLabel(this.step, this.isEditMode);
      },
      footHint() {
        const blocking = this.mappingStatus && this.previewData ? footerHint((this.mappingStatus.blockers || []).filter(isBlocking)) : null;
        return apiFooterHint({
          step: this.step,
          canContinue: this.canContinue,
          testResult: this.testResult,
          mappingHint: blocking,
          schedule: this.schedule,
          hasIdentity: this.hasIdentity,
          registerKind: this.registerKind,
          scheduleInvalid: this.scheduleInvalid,
          saveBlockerCount: this.saveBlockerList.length,
        });
      },
      railStats() {
        return apiRailStats({
          step: this.step,
          testResult: this.testResult,
          previewData: this.previewData,
          mappingStatus: this.previewData ? this.mappingStatus : null,
          schedule: this.schedule,
          hasIdentity: this.hasIdentity,
          identityLabel: this.identityName,
          registerKind: this.registerKind,
          saveBlockerCount: this.saveBlockerList.length,
          fieldCount: this.fieldCount,
        });
      },
      railInfoRows() {
        return apiRailInfoRows({
          register: this.register,
          registerKind: this.registerKind,
          registerRowCount: this.registerRowCount,
          hasFacilityColumn: !!(this.mapping && this.mapping.facilityColumn),
          sourceName: this.config.name,
          step: this.step,
        });
      },
      reviewCardList() {
        return reviewCards({
          config: this.config,
          mapping: this.mapping,
          schedule: this.schedule,
          register: this.register,
          registerKind: this.registerKind,
          hasIdentity: this.hasIdentity,
          identityLabel: this.identityName,
          fieldCount: this.fieldCount,
          identityBlocked: isScheduled(this.schedule.frequency) && (!this.hasIdentity || this.registerUnset),
        });
      },
    },
    watch: {
      value: {
        immediate: true,
        handler(open) {
          this.dialog = open;
          if (open) this.openWizard();
        },
      },
      // A mode-less populated register can't run a first import on save: never send it.
      registerUnset(unset) {
        if (unset && this.schedule.triggerImmediate) {
          this.schedule = { ...this.schedule, triggerImmediate: false };
        }
      },
    },
    methods: {
      openWizard() {
        this.sessionKey += 1;
        this.step = API_STEP.CONFIGURE;
        this.saving = false;
        this.saveError = null;
        this.testResult = null;
        this.previewData = null;
        this.mappingStatus = null;
        if (this.editDataSource) {
          const state = stateFromDataSource(this.editDataSource);
          this.config = state.config;
          this.mapping = state.mapping;
          this.schedule = state.schedule;
        } else {
          this.config = createDefaultConfig();
          this.mapping = null;
          this.schedule = createDefaultSchedule();
        }
        this.loadRegisterRowCount();
      },

      /**
       * Rows of an unset Facility Group register decide empty (the first run sets the mode) vs.
       * populated (only Manual Only can be saved). RegisterVM has no row count, so read the schema's
       * totalRows — as ExcelUpload does. Fails open: the server refuses a populated register anyway.
       */
      async loadRegisterRowCount() {
        const generation = ++this.rowCountGeneration;
        this.registerRowCount = null;
        const reg = this.register;
        if (!needsRowCount(reg)) return;
        try {
          const rows = await loadUnsetRegisterRowCount(reg, registerService);
          if (generation !== this.rowCountGeneration) return;
          this.registerRowCount = rows;
          log('[RegisterApiSource] unset Facility Group register row count', { registerId: reg.id, rows });
        } catch (err) {
          if (generation !== this.rowCountGeneration) return;
          warn('[RegisterApiSource] Could not read the register row count; the server refuses a populated register without a mode.', err);
        }
      },

      next() {
        if (!this.canContinue || this.step >= API_STEP.REVIEW) return;
        this.step += 1;
      },
      back() {
        if (this.step > API_STEP.CONFIGURE) this.goToStep(this.step - 1);
      },
      /** Rail step, Review "Edit", blocker links, "Back to Map Fields": only back to a visited step. */
      goToStep(target) {
        if (this.saving || !Number.isInteger(target) || target < API_STEP.CONFIGURE || target > this.step) return;
        this.saveError = null;
        this.step = target;
      },

      onTestSuccess(result) {
        this.testResult = result;
      },
      onPreviewLoaded(data) {
        this.previewData = data;
        this.previewGeneration += 1;
      },
      onResponseDataPathUpdate(path) {
        this.config = { ...this.config, responseDataPath: path };
      },
      onMappingComplete(mapping) {
        this.mapping = mapping;
      },
      switchToManual() {
        this.schedule = { ...this.schedule, frequency: 0 };
        log('[RegisterApiSource] schedule switched to Manual Only from Map Fields');
      },

      async save() {
        if (!this.canContinue) return;
        this.saving = true;
        this.saveError = null;
        const schedule = this.registerUnset ? { ...this.schedule, triggerImmediate: false } : this.schedule;
        const payload = buildSavePayload({
          config: this.config,
          mapping: this.mapping,
          schedule,
          registerId: this.register && this.register.id,
        });
        try {
          if (this.isEditMode) {
            await this.$store.dispatch('REGISTERS/UPDATE_API_DATA_SOURCE', { id: this.editDataSource.id, data: payload });
            this.$dialog.notify.success('API data source updated');
          } else {
            await this.$store.dispatch('REGISTERS/CREATE_API_DATA_SOURCE', payload);
            this.$dialog.notify.success('API data source created');
          }
          this.saving = false;
          this.close();
          this.$emit('complete');
        } catch (err) {
          const data = err && err.response ? err.response.data : null;
          error('[RegisterApiSource] save refused', { registerId: this.register && this.register.id, code: data && data.code, status: err && err.response && err.response.status }, err);
          this.saveError = saveErrorPresentation(data, (err && err.message) || '');
          this.saving = false;
        }
      },

      confirmClose() {
        if (this.saving) return;
        if (!hasProgress({ step: this.step, config: this.config })) {
          this.close();
          return;
        }
        this.$dialog.confirm({
          text: 'Discard your progress and close?',
          title: 'Close Wizard',
          actions: {
            false: 'Keep Editing',
            true: { text: 'Discard', color: 'error' },
          },
        }).then((confirmed) => {
          if (confirmed) this.close();
        });
      },
      onShellInput(open) {
        if (!open) this.confirmClose();
      },
      close() {
        this.dialog = false;
        this.$emit('input', false);
      },
    },
  };
</script>

<style scoped>
  .ras-steps {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }
</style>
